import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ExchangeIcon } from '@ui/icons/Exchange.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/v2/Table/Table'

import { NameDto } from '@model/NameDto'
import { CurrencyBalance, TradingAccount } from '@model/TradingAccount'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { SupportModal } from '@shared/modals/Support.modal'

import { InitiateConversion, TradingAccountOption } from './TradingAccountBalances'

interface TradingAccountsTableProps {
  balances?: CurrencyBalance[]
  restrictions?: NameDto[]
  isLoading?: boolean
  handleSetSelectedTradingAccount(currency: CurrencyType): void
  initiateTransfer: (currency: CurrencyType) => void
  TAOptionsData?: TradingAccountOption
  setTAOptionsData: (tradingAccountOption?: TradingAccountOption) => void
  initiateConversion: ({ currency, balance }: InitiateConversion) => void
  hasTransferOutRestriction: boolean
}

/**
 * Helper to build the dropdown item "Convert" payload
 */
const getBalanceByCurrency = (account: TradingAccount | undefined, currency: CurrencyType) =>
  account?.platformOverview?.balances?.find((b) => b.currency === currency)

export const TradingAccountsTable: React.FC<TradingAccountsTableProps> = (props) => {
  const {
    balances = [],
    handleSetSelectedTradingAccount,
    isLoading,
    initiateTransfer,
    hasTransferOutRestriction,
    TAOptionsData,
    setTAOptionsData,
    initiateConversion,
  } = props

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  const restrictionModal = useModal(SupportModal, {
    title: t('Temporarily unavailable'),
    description: t('Please contact Support for assistance.'),
  })

  const [showMore, setShowMore] = useState(false)

  const visibleBalances = useMemo(() => {
    if (!balances?.length) return []
    return showMore ? balances : balances.slice(0, 3)
  }, [balances, showMore])

  return (
    <>
      <Table isLoading={!!isLoading} noResults={!visibleBalances.length}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('Trading Account.Currency')}</TableHead>
            <TableHead>{t('Trading Account.Balance')}</TableHead>
            <TableHead />
            <TableHead />
          </TableRow>
        </TableHeader>

        <TableBody>
          {visibleBalances.map((ta) => {
            const isOpen = TAOptionsData?.currency === ta.currency

            return (
              <TableRow key={ta.currency}>
                <TableCell>
                  <Box flex align='center' gap='x8'>
                    {hasTransferOutRestriction && (
                      <IconButton.Ghost
                        size='md'
                        icon={WarningIcon}
                        onClick={() => restrictionModal.open()}
                      />
                    )}
                    {ta.currency}
                  </Box>
                </TableCell>

                <TableCell>
                  <SensitiveData>{formatMoney(ta.balance, ta.currency)}</SensitiveData>
                </TableCell>

                <TableCell>
                  <AlertIconWrapper spaced isActive={hasTransferOutRestriction}>
                    <Tooltip
                      content={t('This action is currently unavailable.')}
                      disabled={!hasTransferOutRestriction}
                    >
                      <Button.Secondary
                        size='sm'
                        disabled={hasTransferOutRestriction}
                        onClick={() => initiateTransfer(ta.currency)}
                      >
                        {t('Transfer')}
                      </Button.Secondary>
                    </Tooltip>
                  </AlertIconWrapper>
                </TableCell>

                <TableCell align='right' hasTruncate={false}>
                  <Popover
                    direction='topRight'
                    distance='0.5rem'
                    isOpen={isOpen}
                    setIsOpen={(open) =>
                      open
                        ? handleSetSelectedTradingAccount(ta.currency)
                        : setTAOptionsData(undefined)
                    }
                    content={
                      <PopoverCard>
                        <PopoverList>
                          <ListItem
                            startSlot={{ icon: ExchangeIcon }}
                            disabled={hasTransferOutRestriction}
                            size='lg'
                            onClick={() => {
                              const selectedBalance = getBalanceByCurrency(
                                TAOptionsData?.tradingAccount,
                                ta.currency
                              )
                              initiateConversion({
                                currency: ta.currency,
                                balance: selectedBalance?.balance,
                                isTransferDisabled: hasTransferOutRestriction,
                              })
                              setTAOptionsData(undefined)
                            }}
                          >
                            {t('Wallet.Convert')}
                          </ListItem>
                        </PopoverList>
                      </PopoverCard>
                    }
                  >
                    <IconButton.Ghost
                      size='md'
                      icon={KebabIcon}
                      onClick={() => handleSetSelectedTradingAccount(ta.currency)}
                    />
                  </Popover>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {balances.length > 3 && (
        <Box mt='x16' justify='center'>
          <Link onClick={() => setShowMore((s) => !s)}>
            {showMore ? t('Show less Currencies') : t('Show more Currencies')}
          </Link>
        </Box>
      )}
    </>
  )
}
